import { useState, useEffect } from 'react';
import {
    Plus,
    SearchLg,
    Eye,
    UserX01,
    Trash01,
    Users01,
    Mail01,
    Package,
    XCircle,
    BookOpen01
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { Button } from '@/components/base/buttons/button';
import { TextArea } from '@/components/base/textarea/textarea';
import { Tabs } from '@/components/application/tabs/tabs';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { useNavigate } from 'react-router';
import { collaborationService } from '@/data/mock-collaborations';
import { containerService } from '@/data/mock-containers';
import { accountService } from '@/data/mock-accounts';
import { addressBookService } from '@/data/mock-address-book';
import type { Collaboration, CollaborationStatus } from '@/types/collaboration';
import type { AddressBookEntry } from '@/types/address-book';
import { useTranslation } from 'react-i18next';

interface ReceiverWithStats {
    id: string;
    legalName: string;
    email: string;
    country: string;
    collaboration: Collaboration;
    activeContainers: number;
    totalContainers: number;
    lastContainerDate?: Date;
}

export const ShipperReceiversPage = () => {
    const { t } = useTranslation(['pages:receivers', 'common']);
    const navigate = useNavigate();
    const [receivers, setReceivers] = useState<ReceiverWithStats[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<CollaborationStatus | ''>('');
    const [countryFilter, setCountryFilter] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteNotes, setInviteNotes] = useState('');
    const [inviteMode, setInviteMode] = useState<'email' | 'addressbook'>('email');
    const [manualEmail, setManualEmail] = useState('');
    const [selectedAddressBookEntry, setSelectedAddressBookEntry] = useState<AddressBookEntry | null>(null);
    const [addressBookEntries, setAddressBookEntries] = useState<AddressBookEntry[]>([]);

    const statusOptions = [
        { id: '', label: t('list.allStatuses', { ns: 'pages:receivers' }) },
        { id: 'Active', label: t('status.active', { ns: 'types' }) },
        { id: 'Pending', label: t('status.pending', { ns: 'types' }) },
        { id: 'Rejected', label: t('status.rejected', { ns: 'types' }) },
        { id: 'Suspended', label: t('status.suspended', { ns: 'types' }) }
    ];

    const countryOptions = [
        { id: '', label: t('list.allCountries', { ns: 'pages:receivers' }) },
        { id: 'United Kingdom', label: 'United Kingdom' },
        { id: 'Netherlands', label: 'Netherlands' },
        { id: 'France', label: 'France' },
        { id: 'Switzerland', label: 'Switzerland' }
    ];

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get current user ID from localStorage
            const accountId = localStorage.getItem('accountId') || '1';

            // Load collaborations for current sender
            const collaborations = collaborationService.getByShipperId(accountId);

            // Load all receiver accounts
            const allAccounts = accountService.getAll();
            const receiverAccounts = allAccounts.filter(acc => acc.userType === 'RECEIVER');

            // Load containers for statistics
            const allContainers = containerService.getAll();
            const senderContainers = allContainers.filter(c => c.shipperId === accountId);

            // Combine data
            const receiversWithStats: ReceiverWithStats[] = collaborations.map(collab => {
                const receiverAccount = receiverAccounts.find(acc => acc.id === collab.receiverId);
                if (!receiverAccount) return null;

                const receiverContainers = senderContainers.filter(c => c.receiverId === collab.receiverId);
                const activeContainers = receiverContainers.filter(c => c.status === 'In Transit' || c.status === 'At Port').length;
                const totalContainers = receiverContainers.length;
                const lastContainer = receiverContainers.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0];

                return {
                    id: receiverAccount.id,
                    legalName: receiverAccount.legalName,
                    email: receiverAccount.email,
                    country: receiverAccount.country,
                    collaboration: collab,
                    activeContainers,
                    totalContainers,
                    lastContainerDate: lastContainer?.createdAt
                };
            }).filter(Boolean) as ReceiverWithStats[];

            setReceivers(receiversWithStats);

            // Load address book entries for current sender
            const entries = addressBookService.getByAccountId(accountId);
            setAddressBookEntries(entries);

            setIsLoading(false);
        };
        loadData();
    }, []);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Pending': return 'warning';
            case 'Rejected': return 'error';
            case 'Suspended': return 'error';
            default: return 'gray';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'Active': return t('status.active', { ns: 'types' });
            case 'Pending': return t('status.pending', { ns: 'types' });
            case 'Rejected': return t('status.rejected', { ns: 'types' });
            case 'Suspended': return t('status.suspended', { ns: 'types' });
            default: return status;
        }
    };

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleInviteReceiver = () => {
        const accountId = localStorage.getItem('accountId') || '1';

        if (inviteMode === 'email') {
            // Modalità email manuale
            if (!manualEmail || !validateEmail(manualEmail)) {
                alert('Inserisci un indirizzo email valido');
                return;
            }

            // Cerca se esiste già un receiver con questa email
            const existingReceiver = accountService.getAll().find(
                acc => acc.userType === 'RECEIVER' && acc.email.toLowerCase() === manualEmail.toLowerCase()
            );

            if (existingReceiver) {
                // Se esiste, crea la collaborazione
                const newCollaboration = collaborationService.create({
                    shipperId: accountId,
                    receiverId: existingReceiver.id,
                    notes: inviteNotes
                });

                if (newCollaboration) {
                    window.location.reload();
                }
            } else {
                // Se non esiste, crea comunque la collaborazione (il backend dovrebbe gestire la creazione del receiver)
                // Per ora, mostriamo un messaggio che indica che il receiver non è ancora nel sistema
                alert(`L'invito sarà inviato a ${manualEmail}. Il receiver verrà aggiunto al sistema quando accetterà l'invito.`);
                // In un'app reale, qui faremmo una chiamata API per inviare l'invito
                setShowInviteModal(false);
                setManualEmail('');
                setInviteNotes('');
            }
        } else {
            // Modalità address book
            if (!selectedAddressBookEntry) {
                alert('Seleziona un contatto dall\'address book');
                return;
            }

            // Cerca se esiste già un receiver con questa email
            const existingReceiver = accountService.getAll().find(
                acc => acc.userType === 'RECEIVER' && acc.email.toLowerCase() === selectedAddressBookEntry.email.toLowerCase()
            );

            if (existingReceiver) {
                const newCollaboration = collaborationService.create({
                    shipperId: accountId,
                    receiverId: existingReceiver.id,
                    notes: inviteNotes
                });

                if (newCollaboration) {
                    window.location.reload();
                }
            } else {
                // Se non esiste, invia l'invito all'email dell'address book
                alert(`L'invito sarà inviato a ${selectedAddressBookEntry.email}. Il receiver verrà aggiunto al sistema quando accetterà l'invito.`);
                setShowInviteModal(false);
                setSelectedAddressBookEntry(null);
                setInviteNotes('');
            }
        }
    };

    const handleSuspendCollaboration = (collaborationId: string) => {
        if (window.confirm(t('list.confirmSuspend', { ns: 'pages:receivers' }))) {
            const success = collaborationService.update(collaborationId, { id: collaborationId, status: 'Suspended' });
            if (success) {
                // Reload data
                window.location.reload();
            }
        }
    };

    const handleDeleteCollaboration = (collaborationId: string) => {
        if (window.confirm(t('list.confirmDelete', { ns: 'pages:receivers' }))) {
            const success = collaborationService.delete(collaborationId);
            if (success) {
                // Reload data
                window.location.reload();
            }
        }
    };

    const filteredReceivers = receivers.filter(receiver => {
        const matchesSearch = !searchTerm ||
            receiver.legalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            receiver.email.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = !statusFilter || receiver.collaboration.status === statusFilter;
        const matchesCountry = !countryFilter || receiver.country === countryFilter;

        return matchesSearch && matchesStatus && matchesCountry;
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('list.title')}</h1>
                    <p className="text-tertiary mt-1">
                        {t('list.subtitle')}
                    </p>
                </div>
                <Button
                    onClick={() => setShowInviteModal(true)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                    iconLeading={<Plus className="size-5" />}
                >
                    {t('list.invite')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Input
                        placeholder={t('list.searchPlaceholder', { ns: 'pages:receivers' })}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('list.statusPlaceholder', { ns: 'pages:receivers' })}
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as CollaborationStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.countryPlaceholder', { ns: 'pages:receivers' })}
                        items={countryOptions}
                        selectedKey={countryFilter}
                        onSelectionChange={(key) => setCountryFilter(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Receivers Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('list.title')} (${filteredReceivers.length})`}
                    description={t('list.description', { ns: 'pages:receivers' })}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('list.headers.company')} isRowHeader />
                        <Table.Head label={t('list.headers.country')} />
                        <Table.Head label={t('list.headers.status')} />
                        <Table.Head label={t('list.headers.activeContainers')} />
                        <Table.Head label={t('list.headers.totalContainers')} />
                        <Table.Head label={t('list.headers.lastContainer')} />
                        <Table.Head label={t('list.headers.inviteDate')} />
                        <Table.Head label={t('list.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {filteredReceivers.map((receiver) => (
                            <Table.Row key={receiver.id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{receiver.legalName}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Package className="size-4 text-tertiary" />
                                        <span className="text-sm text-tertiary">{receiver.country}</span>
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color={getStatusColor(receiver.collaboration.status)} size="sm">
                                        {getStatusLabel(receiver.collaboration.status)}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Package className="size-4 text-tertiary" />
                                        <span className="text-sm text-primary">{receiver.activeContainers}</span>
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{receiver.totalContainers}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {receiver.lastContainerDate
                                            ? receiver.lastContainerDate.toLocaleDateString('it-IT')
                                            : t('list.none')
                                        }
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {receiver.collaboration.invitedAt.toLocaleDateString('it-IT')}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <Dropdown.Root>
                                        <Dropdown.DotsHorizontalButton />
                                        <Dropdown.Popover className="w-min">
                                            <Dropdown.Menu>
                                                <Dropdown.Item
                                                    icon={Eye}
                                                    label={t('list.actions.viewDetails')}
                                                    onAction={() => navigate(`/shipper/receivers/${receiver.id}`)}
                                                />
                                                {receiver.collaboration.status === 'Active' && (
                                                    <Dropdown.Item
                                                        icon={UserX01}
                                                        label={t('list.actions.suspend')}
                                                        onAction={() => handleSuspendCollaboration(receiver.collaboration.id)}
                                                    />
                                                )}
                                                <Dropdown.Item
                                                    icon={Trash01}
                                                    label={t('list.actions.delete')}
                                                    onAction={() => handleDeleteCollaboration(receiver.collaboration.id)}
                                                />
                                            </Dropdown.Menu>
                                        </Dropdown.Popover>
                                    </Dropdown.Root>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {filteredReceivers.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <Users01 className="size-12 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-primary mb-2">{t('list.noReceivers.title')}</h3>
                        <p className="text-tertiary">
                            {searchTerm || statusFilter || countryFilter
                                ? t('list.noReceivers.message')
                                : t('list.noReceivers.emptyMessage')
                            }
                        </p>
                    </div>
                    {!searchTerm && !statusFilter && !countryFilter && (
                        <Button
                            onClick={() => setShowInviteModal(true)}
                            iconLeading={<Plus className="size-5" />}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                        >
                            {t('list.noReceivers.inviteFirst')}
                        </Button>
                    )}
                </div>
            )}

            {/* Invite Modal */}
            {showInviteModal && (
                <ModalOverlay isOpen={showInviteModal} onOpenChange={setShowInviteModal}>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-2xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">{t('list.modal.title')}</h2>
                                    <button
                                        onClick={() => {
                                            setShowInviteModal(false);
                                            setInviteMode('email');
                                            setManualEmail('');
                                            setSelectedAddressBookEntry(null);
                                            setInviteNotes('');
                                        }}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <div className="space-y-6">
                                    {/* Tabs per scegliere modalità */}
                                    <Tabs selectedKey={inviteMode} onSelectionChange={(key) => setInviteMode(key as 'email' | 'addressbook')}>
                                        <Tabs.List items={[
                                            { id: 'email', label: t('list.modal.mode.email') },
                                            { id: 'addressbook', label: t('list.modal.mode.addressBook') }
                                        ]} type="button-border" size="md">
                                            {(item) => (
                                                <Tabs.Item key={item.id} id={item.id}>
                                                    {item.id === 'email' && <Mail01 className="size-4" />}
                                                    {item.id === 'addressbook' && <BookOpen01 className="size-4" />}
                                                    {item.label}
                                                </Tabs.Item>
                                            )}
                                        </Tabs.List>

                                        {/* Tab Email Manuale */}
                                        <Tabs.Panel id="email">
                                            <div className="mt-4">
                                                <label className="text-sm font-medium text-primary mb-2 block">
                                                    {t('list.modal.emailLabel')}
                                                </label>
                                                <Input
                                                    placeholder={t('list.modal.emailPlaceholder')}
                                                    value={manualEmail}
                                                    onChange={setManualEmail}
                                                    size="md"
                                                    icon={Mail01}
                                                    type="email"
                                                />
                                                <p className="text-xs text-tertiary mt-2">
                                                    {t('list.modal.emailHint')}
                                                </p>
                                            </div>
                                        </Tabs.Panel>

                                        {/* Tab Address Book */}
                                        <Tabs.Panel id="addressbook">
                                            <div className="mt-4">
                                                <label className="text-sm font-medium text-primary mb-2 block">
                                                    {t('list.modal.selectFromAddressBook')}
                                                </label>
                                                <Select
                                                    placeholder={t('list.modal.selectAddressBookPlaceholder')}
                                                    items={addressBookEntries.map(entry => ({
                                                        id: entry.id,
                                                        label: `${entry.firstName} ${entry.lastName} (${entry.email})`
                                                    }))}
                                                    selectedKey={selectedAddressBookEntry?.id || ''}
                                                    onSelectionChange={(key) => {
                                                        const entry = addressBookEntries.find(e => e.id === key);
                                                        setSelectedAddressBookEntry(entry || null);
                                                    }}
                                                    size="md"
                                                >
                                                    {(item) => (
                                                        <Select.Item key={item.id} id={item.id}>
                                                            {item.label}
                                                        </Select.Item>
                                                    )}
                                                </Select>
                                            </div>
                                        </Tabs.Panel>
                                    </Tabs>

                                    <div>
                                        <TextArea
                                            label={t('list.modal.notes')}
                                            placeholder={t('list.modal.notesPlaceholder')}
                                            value={inviteNotes}
                                            onChange={setInviteNotes}
                                        />
                                    </div>

                                    {/* Preview informazioni selezionate */}
                                    {(inviteMode === 'email' && manualEmail && validateEmail(manualEmail)) ||
                                        (inviteMode === 'addressbook' && selectedAddressBookEntry) ? (
                                        <div className="rounded-lg bg-secondary p-4">
                                            <h4 className="text-sm font-medium text-primary mb-2">{t('list.modal.selectedInfo')}</h4>
                                            <div className="text-sm text-tertiary">
                                                {inviteMode === 'email' ? (
                                                    <>
                                                        <div><strong>{t('list.modal.email')}:</strong> {manualEmail}</div>
                                                    </>
                                                ) : selectedAddressBookEntry ? (
                                                    <>
                                                        <div><strong>{t('list.modal.name')}:</strong> {selectedAddressBookEntry.firstName} {selectedAddressBookEntry.lastName}</div>
                                                        <div><strong>{t('list.modal.email')}:</strong> {selectedAddressBookEntry.email}</div>
                                                        <div><strong>{t('list.modal.role')}:</strong> {selectedAddressBookEntry.role}</div>
                                                    </>
                                                ) : null}
                                            </div>
                                        </div>
                                    ) : null}

                                    <div className="flex justify-end gap-3">
                                        <Button
                                            onClick={() => {
                                                setShowInviteModal(false);
                                                setInviteMode('email');
                                                setManualEmail('');
                                                setSelectedAddressBookEntry(null);
                                                setInviteNotes('');
                                            }}
                                            color="secondary"
                                            size="md"
                                        >
                                            {t('list.modal.cancel')}
                                        </Button>
                                        <Button
                                            onClick={handleInviteReceiver}
                                            disabled={
                                                (inviteMode === 'email' && (!manualEmail || !validateEmail(manualEmail))) ||
                                                (inviteMode === 'addressbook' && !selectedAddressBookEntry)
                                            }
                                            color="primary"
                                            size="md"
                                        >
                                            {t('list.modal.sendInvite')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};
